import { useContext, useEffect, useMemo, useRef } from 'react'

import { fetchExperienceListFacets, fetchExperienceSearchList } from 'actions/ExperienceActions'
import GeoContext from 'contexts/geoContext'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { usePlaceByCoords } from 'hooks/usePlace'
import { getExperienceListKey, getExperiencePlaceShortName, getExperienceSearchPath } from 'lib/experiences/experienceUtils'
import { getDiscoverNearDestinations } from 'lib/experiences/experiencesHomepageUtils'
import getObjectKey from 'lib/object/getObjectKey'

export interface ExperiencesHomepageDiscoverNearData {
  placeName: string
  searchUrl: string
  totalOfferCount: number
  destinations: Array<{
    key: string
    title: string
    offerCount?: number
    image: { id: string, title: string }
    to: string
  }>
  fetching: boolean
}

const EMPTY_AREAS: ReadonlyArray<App.ExperienceAreaFacet> = []
const DISCOVER_NEAR_PLACE_LEVELS: Array<App.PlaceType> = ['multi_city_vicinity', 'city']

function useExperiencesHomepageDiscoverNearData(): ExperiencesHomepageDiscoverNearData | null {
  const dispatch = useAppDispatch()
  const geo = useContext(GeoContext)
  const experienceLists = useAppSelector((state) => state.experience.experienceLists)
  const experienceListFacets = useAppSelector((state) => state.experience.experienceListFacets)
  const [anchorPlace] = usePlaceByCoords(
    geo.latitude,
    geo.longitude,
    DISCOVER_NEAR_PLACE_LEVELS,
  )

  const anchorSearchList = useExperienceSearchList({
    placeId: anchorPlace?.id,
    sortBy: 'type',
    enabled: !!anchorPlace?.id,
  })

  const facetKey = anchorPlace ? getObjectKey({ placeId: anchorPlace.id }) : ''
  const facetsState = facetKey ? experienceListFacets[facetKey] : undefined

  const areas = facetsState?.facets?.areas ?? EMPTY_AREAS
  const anchorFacetsFetching = Boolean(anchorPlace && (!facetsState || facetsState.fetching))

  const areasRefetchAttemptedForPlaceId = useRef<string | undefined>(undefined)

  const rawDestinations = useMemo(() => {
    if (!anchorPlace) {
      return []
    }

    return getDiscoverNearDestinations(anchorPlace.id, areas)
  }, [anchorPlace, areas])

  const destinationListPlaceIds = useMemo(() => {
    return Array.from(new Set(rawDestinations.map((destination) => destination.placeId)))
  }, [rawDestinations])

  useEffect(() => {
    if (!anchorPlace?.id) {
      return
    }

    dispatch(fetchExperienceListFacets(anchorPlace.id))
  }, [anchorPlace?.id, dispatch])

  useEffect(() => {
    if (!anchorPlace?.id || anchorFacetsFetching) {
      return
    }

    const hasLoadedFacets = facetsState?.facets !== undefined
    const areasCount = facetsState?.facets?.areas?.length ?? 0

    if (
      hasLoadedFacets
      && areasCount === 0
      && areasRefetchAttemptedForPlaceId.current !== anchorPlace.id
    ) {
      areasRefetchAttemptedForPlaceId.current = anchorPlace.id
      dispatch(fetchExperienceListFacets(anchorPlace.id, {}, undefined, { force: true }))
    }
  }, [anchorPlace?.id, dispatch, anchorFacetsFetching, facetsState])

  useEffect(() => {
    destinationListPlaceIds.forEach((placeId) => {
      dispatch(fetchExperienceSearchList(placeId))
    })
  }, [destinationListPlaceIds, dispatch])

  const destinationListsFetching = useMemo(() => {
    if (destinationListPlaceIds.length === 0) {
      return false
    }

    return destinationListPlaceIds.some((placeId) => {
      const destinationListKey = getExperienceListKey({ placeId })
      const destinationListState = experienceLists[destinationListKey]
      return !destinationListState || destinationListState.fetching
    })
  }, [destinationListPlaceIds, experienceLists])

  return useMemo(() => {
    if (!anchorPlace) {
      return null
    }

    const placeName = getExperiencePlaceShortName(anchorPlace)
    const searchUrl = getExperienceSearchPath(anchorPlace)

    const destinations = rawDestinations.map((destination) => {
      const place: App.Place = {
        id: destination.placeId,
        name: destination.name,
        type: destination.type as App.Place['type'],
        countryCode: destination.countryCode,
      }

      const destinationListKey = getExperienceListKey({ placeId: destination.placeId })
      const destinationListState = experienceLists[destinationListKey]
      const offerCount = destinationListState?.error
        ? undefined
        : destinationListState?.ids.length

      return {
        key: destination.placeId,
        title: destination.name,
        offerCount,
        image: { id: destination.imageId, title: destination.name },
        to: getExperienceSearchPath(place),
      }
    })

    const totalOfferCount = anchorSearchList.fetching ? 0 : anchorSearchList.ids.length
    const fetching =
      anchorFacetsFetching || destinationListsFetching || Boolean(anchorSearchList.fetching)

    if (!fetching && destinations.length === 0) {
      return null
    }

    return {
      placeName,
      searchUrl,
      totalOfferCount,
      destinations,
      fetching,
    }
  }, [
    anchorFacetsFetching,
    anchorPlace,
    anchorSearchList.fetching,
    anchorSearchList.ids.length,
    destinationListsFetching,
    experienceLists,
    rawDestinations,
  ])
}

export default useExperiencesHomepageDiscoverNearData
